import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Database, 
  RefreshCw, 
  Search, 
  Calendar, 
  ShieldAlert, 
  CheckCircle, 
  AlertTriangle, 
  Trash2,
  ListFilter
} from "lucide-react";

interface AuditLog {
  id: string;
  timestamp: string;
  module: string;
  action: string;
  status: string;
  details: string;
}

export const SecurityAuditLog: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL");

  const loadLogs = () => {
    const stored = localStorage.getItem("nexorax_audit_logs");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          // Filter out invalid items and sort by timestamp descending just in case
          const sanitized: AuditLog[] = parsed
            .filter((item: any) => item && typeof item === "object")
            .map((item: any) => ({
              id: item.id || `log-${Date.now()}-${Math.random()}`,
              timestamp: item.timestamp || new Date().toISOString(),
              module: item.module || "SYSTEM",
              action: item.action || "UNKNOWN ACTION",
              status: item.status || "INFO",
              details: item.details || ""
            }))
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          
          // Display the 20 most recent events
          setLogs(sanitized.slice(0, 20));
        }
      } catch (err) {
        console.error("Failed to parse nexorax_audit_logs from localStorage:", err);
      }
    }
  };

  useEffect(() => {
    loadLogs();
    // Setup listener to sync if localStorage is modified from other actions
    const handleStorageChange = () => {
      loadLogs();
    };
    window.addEventListener("storage", handleStorageChange);
    
    // Auto-refresh interval of 8 seconds to match other system status updates
    const interval = setInterval(loadLogs, 8000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const clearLogsAndReset = () => {
    if (window.confirm("ARE YOU SURE YOU WANT TO SHIELD AND REMOVE THE LOCAL CACHED SECURITY LEDGER?")) {
      localStorage.setItem("nexorax_audit_logs", JSON.stringify([]));
      setLogs([]);
    }
  };

  const getStatusStyle = (status: string) => {
    const s = status.toUpperCase();
    if (s.includes("SECURED") || s.includes("SUCCESS") || s === "OK") {
      return {
        bg: "bg-emerald-950/40",
        border: "border-emerald-500/30",
        text: "text-emerald-400",
        icon: <CheckCircle className="w-3 h-3 text-emerald-400" />
      };
    } else if (s.includes("BLOCK") || s.includes("FAIL") || s.includes("DENI") || s.includes("CRIT")) {
      return {
        bg: "bg-rose-950/40",
        border: "border-rose-500/30",
        text: "text-rose-400",
        icon: <ShieldAlert className="w-3 h-3 text-rose-400" />
      };
    } else {
      return {
        bg: "bg-amber-950/40",
        border: "border-amber-500/30",
        text: "text-amber-400",
        icon: <AlertTriangle className="w-3 h-3 text-amber-450" />
      };
    }
  };

  // Filter logs based on search query and status drop-down
  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.module.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      selectedStatus === "ALL" || 
      (selectedStatus === "SECURED" && (log.status.toUpperCase().includes("SECURED") || log.status.toUpperCase().includes("SUCCESS"))) ||
      (selectedStatus === "BLOCKED" && (log.status.toUpperCase().includes("BLOCK") || log.status.toUpperCase().includes("FAIL") || log.status.toUpperCase().includes("DENI"))) ||
      (selectedStatus === "WARNING" && (!log.status.toUpperCase().includes("SECURED") && !log.status.toUpperCase().includes("SUCCESS") && !log.status.toUpperCase().includes("BLOCK") && !log.status.toUpperCase().includes("FAIL") && !log.status.toUpperCase().includes("DENI")));

    return matchesSearch && matchesStatus;
  });

  return (
    <div id="security-audit-log-root" className="w-full relative">
      {/* 
        PURPLE-GLASSMORPHISM PANEL WITH DEEP MATTE GLOSSING & INTENTIONAL ACCENTUATIONS
      */}
      <div 
        id="security-audit-log-panel" 
        className="glass-panel border border-purple-500/35 rounded-3xl bg-[#030616]/75 p-6 shadow-[0_0_50px_rgba(168,85,247,0.18)] relative overflow-hidden backdrop-blur-xl"
      >
        {/* Glow Spheres */}
        <div className="absolute -top-32 -left-32 w-80 h-80 bg-purple-600/10 blur-[80px] rounded-full pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-fuchsia-600/5 blur-[80px] rounded-full pointer-events-none" />
        {/* Sleek top ambient accent line */}
        <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-purple-500 via-fuchsia-500 to-indigo-500" />

        {/* Panel Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 pb-4 border-b border-slate-800/80 mb-5">
          <div className="text-left">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-purple-950/50 border border-purple-500/40 rounded-xl text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.3)]">
                <Database className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-sm font-black text-purple-200 tracking-[0.2em] uppercase flex items-center gap-2">
                  SECURITY AUDIT LOG
                  <span className="text-[8.5px] bg-[#02050c] text-purple-400 border border-purple-500/30 px-2 py-0.5 rounded-full font-bold tracking-widest">
                    ACTIVE SENTRY
                  </span>
                </h3>
                <p className="text-[10px] text-zinc-500 uppercase font-medium tracking-wide mt-0.5">
                  Secure localized ledger tracking the 20 most recent transactional system events
                </p>
              </div>
            </div>
          </div>

          {/* Interactive controls */}
          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto font-mono text-[9.5px]">
            <button
              id="btn-refresh-audit"
              onClick={loadLogs}
              style={{ cursor: "pointer" }}
              className="px-3.5 py-2 bg-purple-950/20 border border-purple-500/30 text-purple-300 hover:text-white hover:bg-purple-500 hover:border-purple-400 transition-all rounded-xl font-bold uppercase tracking-wider cursor-pointer flex items-center gap-1.5 shadow-[0_0_15px_rgba(168,85,247,0.05)]"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Sync Ledger
            </button>
            <button
              id="btn-clear-audit"
              onClick={clearLogsAndReset}
              style={{ cursor: "pointer" }}
              className="px-3.5 py-2 bg-rose-950/20 border border-rose-500/20 text-rose-400 hover:text-white hover:bg-rose-600 hover:border-rose-500 transition-all rounded-xl font-bold uppercase tracking-wider cursor-pointer flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Shield Cache
            </button>
          </div>
        </div>

        {/* Search & Filtering Row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-5 font-mono">
          <div className="md:col-span-8 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              id="audit-search-input"
              type="text"
              placeholder="SEARCH SECURITY MODULE, ACTIONS, OR PAYLOAD DETAILS..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#02050c]/90 border border-slate-800/80 rounded-xl text-xs text-white placeholder-slate-600 focus:outline-none focus:border-purple-500/60 transition uppercase font-mono tracking-wider"
            />
          </div>
          <div className="md:col-span-4 relative flex items-center gap-2 bg-[#02050c]/90 border border-slate-800/80 rounded-xl px-3.5">
            <ListFilter className="w-4 h-4 text-slate-500 shrink-0" />
            <select
              id="audit-status-filter"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full py-2.5 bg-transparent border-0 text-slate-300 text-[10.5px] uppercase font-mono tracking-wider focus:outline-none cursor-pointer"
            >
              <option value="ALL" className="bg-[#02050a] text-slate-300">ALL STATUS SEVERITIES</option>
              <option value="SECURED" className="bg-[#02050a] text-emerald-400">SECURED / SUCCESS ONLY</option>
              <option value="WARNING" className="bg-[#02050a] text-amber-400">WARNING / PENDING ONLY</option>
              <option value="BLOCKED" className="bg-[#02050a] text-rose-400">BLOCKED / SHIELDED ONLY</option>
            </select>
          </div>
        </div>

        {/* Dynamic Metric Grid inside the Purple Glass panel */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5 font-mono text-[9px] text-left">
          <div className="p-3 rounded-2xl bg-purple-950/15 border border-purple-500/15">
            <span className="text-purple-400 font-extrabold uppercase tracking-widest block">Ledger Loadout</span>
            <span className="text-white text-base font-black mt-1 block">{logs.length} <span className="text-[10px] text-zinc-500 font-bold">/ 20</span></span>
          </div>
          <div className="p-3 rounded-2xl bg-emerald-950/15 border border-emerald-500/15">
            <span className="text-emerald-400 font-extrabold uppercase tracking-widest block">Secured Actions</span>
            <span className="text-white text-base font-black mt-1 block">
              {logs.filter(l => {
                const s = l.status.toUpperCase();
                return s.includes("SECURED") || s.includes("SUCCESS") || s === "OK";
              }).length}
            </span>
          </div>
          <div className="p-3 rounded-2xl bg-amber-950/15 border border-amber-500/15">
            <span className="text-amber-400 font-extrabold uppercase tracking-widest block">Active Alerts</span>
            <span className="text-white text-base font-black mt-1 block">
              {logs.filter(l => {
                const s = l.status.toUpperCase();
                return !s.includes("SECURED") && !s.includes("SUCCESS") && !s.includes("BLOCK") && !s.includes("FAIL") && !s.includes("DENI");
              }).length}
            </span>
          </div>
          <div className="p-3 rounded-2xl bg-rose-950/15 border border-rose-500/15">
            <span className="text-rose-400 font-extrabold uppercase tracking-widest block">Blocked Intrusion</span>
            <span className="text-white text-base font-black mt-1 block">
              {logs.filter(l => {
                const s = l.status.toUpperCase();
                return s.includes("BLOCK") || s.includes("FAIL") || s.includes("DENI");
              }).length}
            </span>
          </div>
        </div>

        {/* Table Body Frame */}
        <div className="overflow-x-auto rounded-2xl border border-slate-800/80 bg-black/45 shadow-inner">
          <table className="w-full min-w-[700px] border-collapse text-left text-xs font-mono select-none">
            <thead>
              <tr className="border-b border-slate-800/90 bg-[#02050c]/60 text-slate-400 text-[10px] font-black uppercase tracking-wider">
                <th className="py-3.5 px-4 w-[160px] flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Timestamp</th>
                <th className="py-3.5 px-4 w-[150px]">Module Sector</th>
                <th className="py-3.5 px-4 w-[120px]">Event Severity</th>
                <th className="py-3.5 px-4">Action &amp; Target Ledger payload Details</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence initial={false}>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-12 text-center text-slate-500 italic uppercase tracking-widest font-bold">
                      --- No system compliance logs match the active parameters ---
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => {
                    const style = getStatusStyle(log.status);
                    return (
                      <motion.tr
                        key={log.id}
                        initial={{ opacity: 0, y: 3 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="border-b border-slate-900/60 hover:bg-[#060412]/30 transition-colors group align-middle"
                      >
                        {/* Timestamp */}
                        <td className="py-3 px-4 text-slate-400 font-bold whitespace-nowrap align-middle">
                          <span className="text-zinc-500 font-black">
                            {new Date(log.timestamp).toLocaleDateString()}
                          </span>
                          <span className="text-purple-400/80 font-medium ml-1.5">
                            {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </span>
                        </td>

                        {/* Module Sector */}
                        <td className="py-3 px-4 font-black align-middle text-purple-300">
                          <span className="px-2 py-0.5 rounded-md bg-[#02050c] border border-slate-800 text-[9.5px]">
                            {log.module}
                          </span>
                        </td>

                        {/* Event Severity Status */}
                        <td className="py-3 px-4 align-middle">
                          <span className={`px-2.5 py-1 rounded-full text-[9px] font-black tracking-wider uppercase border flex items-center gap-1.5 inline-flex ${style.bg} ${style.border} ${style.text}`}>
                            {style.icon}
                            {log.status === "SUCCESSFUL" ? "SECURED" : log.status}
                          </span>
                        </td>

                        {/* Action Details */}
                        <td className="py-3 px-4 font-sans align-middle">
                          <div className="flex flex-col text-left">
                            <span className="text-[11.5px] font-bold text-white uppercase tracking-tight font-sans">
                              {log.action}
                            </span>
                            {log.details && (
                              <span className="text-[9.5px] font-mono text-slate-500 mt-0.5 leading-relaxed capitalize">
                                {log.details}
                              </span>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {/* Lower Ledger Status Block */}
        <div className="mt-4 flex flex-col md:flex-row justify-between items-center text-[8.5px] text-slate-500 uppercase tracking-widest gap-2 font-bold select-none">
          <span>Enterprise Secure Frame Matrix Active (SC887095)</span>
          <span>Filtered Events: {filteredLogs.length} of {logs.length} (Ledger Limit: 20)</span>
        </div>
      </div>
    </div>
  );
};
